import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { pdfExportService } from '../../../services/pdfExportService';

import { snagService } from '../../../services/snagService';
import { documentService } from '../../../services/documentService';
import { activityService } from '../../../services/activityService';
import { dailyTaskService } from '../../../services/taskService';
import { companyService } from '../../../services/companyService';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import { notificationService } from '../../../services/notificationService';
import { useAuth } from '../../../contexts/AuthContext';

const SiteReportExportModal = ({ isOpen, onClose, sites = [] }) => {
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [generating, setGenerating] = useState(false);
  const { userProfile } = useAuth();

  if (!isOpen) return null;

  const handleExport = async () => {
    if (!selectedSiteId) {
      toast?.error('Please select a site');
      return;
    }
    setGenerating(true);
    try {
      const site = sites?.find(s => s?.id === selectedSiteId);

      const [
        companyRes,
        snagsRes,
        docsRes,
        activitiesRes,
        tasksRes,
        photosRes,
      ] = await Promise.all([
        companyService?.getUserCompany(),
        snagService?.getAllSnags(),
        documentService?.getAllDocuments(),
        activityService?.getAllActivities(),
        dailyTaskService?.getByDateAndSite(null, selectedSiteId),
        supabase
          ?.from('task_completion_photos')
          ?.select('id, photo_url, caption, created_at, task_id')
          ?.eq('site_id', selectedSiteId)
          ?.order('created_at', { ascending: false })
          ?.limit(20),
      ]);

      const company = companyRes?.data;
      const allSnags = snagsRes?.data || [];
      const allDocs = docsRes?.data || [];
      const allActivities = activitiesRes?.data || [];
      const allTasks = tasksRes?.data || [];
      const photos = photosRes?.data || [];

      const siteSnags = allSnags?.filter(s => s?.site_id === selectedSiteId);
      const siteDocs = allDocs?.filter(d => d?.site_id === selectedSiteId);
      const siteActivities = allActivities?.filter(a => a?.site_id === selectedSiteId);
      const siteTasks = allTasks?.filter(t => t?.site_id === selectedSiteId);

      // Fetch site manager name
      let siteManagerName = '—';
      if (site?.site_manager_id) {
        const { data: managerProfile } = await supabase
          ?.from('user_profiles')
          ?.select('full_name, email')
          ?.eq('id', site?.site_manager_id)
          ?.maybeSingle();
        siteManagerName = managerProfile?.full_name || managerProfile?.email || '—';
      }

      await pdfExportService?.generateSiteAuditReport({
        site: { ...site, site_manager_name: siteManagerName },
        company,
        snags: siteSnags,
        documents: siteDocs,
        activities: siteActivities,
        tasks: siteTasks,
        photos,
      });

      // Notify admins that a report was exported
      notificationService?.notifyReportExported(
        company?.id,
        selectedSiteId,
        site?.site_name,
        userProfile?.full_name || null
      )?.catch(() => {});

      toast?.success(`Report exported for ${site?.site_name || 'site'}`);
      onClose();
    } catch (err) {
      console.error('Site report export error:', err);
      toast?.error('Failed to generate the report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="FileDown" size={18} className="text-accent" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground font-heading">Export Site Report</h2>
              <p className="text-xs text-muted-foreground">Generate a PDF audit report for a site</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Select Site</label>
            <select
              value={selectedSiteId}
              onChange={e => setSelectedSiteId(e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            >
              <option value="">— Choose a site —</option>
              {sites?.map(site => (
                <option key={site?.id} value={site?.id}>{site?.site_name}</option>
              ))}
            </select>
          </div>

          <div className="bg-muted/40 rounded-lg p-4 space-y-1.5">
            <p className="text-xs font-medium text-foreground mb-2">Report includes:</p>
            {[
              'Site name & site manager',
              'Completed tasks',
              'Uploaded photos',
              'Open & closed issues/snags',
              'Document status',
              'Activity timeline',
            ]?.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Check" size={12} className="text-success flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={generating}>Cancel</Button>
          <Button onClick={handleExport} loading={generating} disabled={!selectedSiteId || generating}>
            <Icon name="Download" size={16} />
            Export PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SiteReportExportModal;
